import React, { useState } from 'react';
import { ChevronLeft, ChevronRight, Calendar } from 'lucide-react';
import { Button } from './ui/button';

/**
 * DateNavigator Component - Admin only
 * Allows navigation between dates to compare content for duplicate checking
 */
const DateNavigator = ({ currentDate, onDateChange, isAdmin, sectionKey, backendUrl, token }) => {
  const [historicalContent, setHistoricalContent] = useState(null);
  const [viewingDate, setViewingDate] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Only show for admin users
  if (!isAdmin) return null;

  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    const date = new Date(dateStr);
    return date.toLocaleDateString('el-GR', { 
      weekday: 'short', 
      day: 'numeric', 
      month: 'short' 
    });
  };

  const getDateOffset = (baseDate, offset) => {
    const date = new Date(baseDate);
    date.setDate(date.getDate() + offset);
    return date.toISOString().split('T')[0];
  };

  const fetchHistoricalContent = async (targetDate) => {
    setLoading(true);
    setError(null);
    
    console.log('[DateNavigator] Fetching content for:', targetDate);
    console.log('[DateNavigator] Backend URL:', backendUrl);
    console.log('[DateNavigator] Token:', token ? 'present' : 'missing');
    
    try {
      // Use correct endpoint: /api/content/{date}
      const response = await fetch(`${backendUrl}/api/content/${targetDate}`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      
      console.log('[DateNavigator] Response status:', response.status);
      
      if (response.ok) {
        const data = await response.json();
        console.log('[DateNavigator] Data received, sections:', Object.keys(data.sections || data.content || {}).length);
        // Handle both 'sections' and 'content' keys
        const sections = data.sections || data.content || {};
        setHistoricalContent({ ...data, sections });
        setViewingDate(targetDate);
      } else if (response.status === 404) {
        console.log('[DateNavigator] 404 - No content for date');
        setError(`Δεν υπάρχει περιεχόμενο για ${targetDate}`);
        setHistoricalContent(null);
      } else {
        console.log('[DateNavigator] Other error:', response.status);
        setError('Σφάλμα κατά τη φόρτωση');
      }
    } catch (err) {
      console.error('[DateNavigator] Fetch error:', err);
      setError('Σφάλμα σύνδεσης');
    } finally {
      setLoading(false);
    }
  };

  const navigateDate = (offset) => {
    const baseDate = viewingDate || currentDate;
    const newDate = getDateOffset(baseDate, offset);
    fetchHistoricalContent(newDate);
  };

  const resetToToday = () => {
    setHistoricalContent(null);
    setViewingDate(null);
    setError(null);
  };

  // Get section content from historical data
  const getHistoricalSectionContent = () => {
    if (!historicalContent) return null;
    // Handle both 'sections' and 'content' keys from API response
    const sections = historicalContent.sections || historicalContent.content || {};
    return sections[sectionKey];
  };

  const historicalSection = getHistoricalSectionContent();

  return (
    <div className="mt-3 border-t border-gray-200 pt-3">
      {/* Navigation Controls */}
      <div className="flex items-center justify-between gap-2 mb-2">
        <div className="flex items-center gap-1">
          <Button
            variant="outline"
            size="sm"
            onClick={() => navigateDate(-1)}
            disabled={loading}
            className="h-8 w-8 p-0"
            title="Προηγούμενη μέρα"
          >
            <ChevronLeft className="h-4 w-4" />
          </Button>
          
          <div className="flex items-center gap-1 px-2 py-1 bg-gray-100 rounded text-xs font-medium">
            <Calendar className="h-3 w-3" />
            {viewingDate ? (
              <span className="text-blue-600">{formatDate(viewingDate)}</span>
            ) : (
              <span className="text-gray-600">Σήμερα ({formatDate(currentDate)})</span>
            )}
          </div>
          
          <Button
            variant="outline"
            size="sm"
            onClick={() => navigateDate(1)}
            disabled={loading || (!viewingDate && true)}
            className="h-8 w-8 p-0"
            title="Επόμενη μέρα"
          >
            <ChevronRight className="h-4 w-4" />
          </Button>
        </div>
        
        {viewingDate && (
          <Button
            variant="ghost"
            size="sm"
            onClick={resetToToday}
            className="text-xs h-7 text-blue-600 hover:text-blue-800"
          >
            ↩ Επιστροφή στο Σήμερα
          </Button>
        )}
      </div>

      {/* Loading State */}
      {loading && (
        <div className="text-center py-2 text-sm text-gray-500">
          ⏳ Φόρτωση...
        </div>
      )}

      {/* Error State */}
      {error && (
        <div className="text-center py-2 text-sm text-orange-600">
          ⚠️ {error}
        </div>
      )}

      {/* Historical Content Preview - Enhanced for more content */}
      {historicalSection && viewingDate && (
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mt-2">
          <div className="flex items-center justify-between mb-3">
            <span className="text-sm font-bold text-blue-700">
              📅 Περιεχόμενο: {formatDate(viewingDate)}
            </span>
            <span className="text-xs text-blue-600 bg-blue-100 px-2 py-1 rounded">
              {historicalSection.content?.length || 0} χαρακτήρες
            </span>
          </div>
          <div className="text-sm text-gray-700 max-h-96 overflow-y-auto bg-white p-3 rounded border">
            <pre className="whitespace-pre-wrap font-sans leading-relaxed">
              {historicalSection.content?.substring(0, 2000)}
              {historicalSection.content?.length > 2000 && (
                <span className="text-blue-500 font-semibold">
                  \n\n... και {historicalSection.content.length - 2000} ακόμη χαρακτήρες
                </span>
              )}
            </pre>
          </div>
          {/* Additional metadata if available */}
          {historicalSection.title && (
            <div className="mt-2 text-xs text-gray-500">
              <strong>Τίτλος:</strong> {historicalSection.title}
            </div>
          )}
        </div>
      )}

      {/* Admin Badge */}
      <div className="text-right mt-1">
        <span className="text-xs text-gray-400 italic">
          🔐 Admin: Πλοήγηση ημερομηνιών
        </span>
      </div>
    </div>
  );
};

export default DateNavigator;
